// src/components/AwardsCarousel.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Section } from "./sharedStyles";
import Slide1 from "../images/slide1.jpeg";
import Slide2 from "../images/slide2.jpeg";
import Slide3 from "../images/slide3.jpeg";
import Slide4 from "../images/slide4.jpeg";
import Slide5 from "../images/slide5.jpeg";
import Slide6 from "../images/slide6.jpeg";
import Slide7 from "../images/slide7.jpeg";
import Slide8 from "../images/slide8.jpeg";
import Slide9 from "../images/slide9.jpeg";
/* -------------------------
   Styled: container + viewport
   ------------------------- */
const Container = styled(Section)`
  text-align: center;
  padding-left: 20px;
  padding-right: 20px;

  /* dark themed background from theme (fallback provided) */
  background: ${(p) =>
    p.theme.gradients?.bgSection ||
    `linear-gradient(180deg, ${p.theme.colors.bgDarker}, ${p.theme.colors.bgDark})`};
  color: ${(p) => p.theme.colors.text};

  @media (min-width: 720px) {
    padding-left: 32px;
    padding-right: 32px;
  }

  @media (min-width: 1100px) {
    padding-left: 48px;
    padding-right: 48px;
  }

  h2 {
    margin: 0 0 8px 0;
    color: ${(p) => p.theme.colors.text};
  }

  p {
    margin: 0;
    color: ${(p) => p.theme.colors.muted};
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Viewport = styled.div`
  position: relative;
  margin-top: 20px;
  overflow: hidden;
`;

/* -------------------------
   Slides & frame
   ------------------------- */
const Slides = styled.div`
  display: flex;
  transition: transform 480ms cubic-bezier(0.2, 0.9, 0.2, 1);
  will-change: transform;
`;

/* each frame: keeps area for the "hero" card */
const Frame = styled.figure`
  flex: 0 0 100%;
  padding: 12px;
  box-sizing: border-box;
  margin: 0;
  height: 600px;

  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 520px) {
    height: 600px;
  }
  @media (min-width: 900px) {
    height: 600px;
  }
`;

/* -------------------------
   Attractive Card (dark friendly)
   ------------------------- */
const Card = styled.div`
  width: 100%;
  max-width: 820px;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  transition: transform 260ms ease, box-shadow 260ms ease;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.04);

  /* darker card body so images and overlay read well */
  background: ${(p) =>
    p.theme.colors.card ||
    `linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))`};

  &:hover {
    transform: translateY(-8px) scale(1.01);
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.7);
  }

  /* subtle colored rim using theme */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: 16px;
    padding: 1px;
    background: linear-gradient(
      90deg,
      ${(p) => p.theme.colors.primary} 0%,
      ${(p) => p.theme.colors.accent} 40%,
      ${(p) => p.theme.colors.secondary} 100%
    );
    -webkit-mask: linear-gradient(#fff, #fff) content-box,
      linear-gradient(#fff, #fff);
    mask: linear-gradient(#fff, #fff) content-box, linear-gradient(#fff, #fff);
    mask-composite: exclude;
    opacity: 0.06;
  }
`;

/* image box */
const ImgBox = styled.div`
  flex: 1 1 100%;
  position: relative;
  overflow: hidden;
  background: ${(p) => p.theme.colors.bgDark};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain; /* ⭐ Show entire image in box */
    display: block;
    transform-origin: center;
    transition: transform 0.3s ease;
  }

  /* Remove zoom completely */
  ${Card}:hover & img {
    transform: none;
  }

  @media (max-width: 899px) {
    flex-basis: 100%;
    height: 100%;
  }
`;

/* content panel (right) */
/* top-left badge (index) */
const Badge = styled.div`
  position: absolute;
  left: 16px;
  top: 16px;
  z-index: 6;
  background: ${(p) => p.theme.gradients?.primary || p.theme.colors.primary};
  color: ${(p) => p.theme.colors.bgDark};
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.9rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
`;

/* subtle title overlay on image (small, top-right) */
/* -------------------------
   Controls + dots (theme-aware)
   ------------------------- */
const Arrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: ${(p) => (p.transparent ? "transparent" : p.theme.colors.text)};
  border: 0;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28);
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  z-index: 50;
  color: ${(p) => p.theme.colors.bgDark};
  transition: transform 160ms ease, opacity 160ms ease;

  &:hover {
    transform: translateY(-50%) scale(1.03);
    opacity: 0.98;
  }

  &[data-dir="prev"] {
    left: 12px;
  }
  &[data-dir="next"] {
    right: 12px;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: ${(p) => p.theme.colors.bgDark};
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 14px;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${(p) =>
    p.active ? p.theme.colors.accent : "rgba(255,255,255,0.06)"};
  border: 0;
  cursor: pointer;
  transition: transform 120ms ease, opacity 120ms ease;
  opacity: ${(p) => (p.active ? 1 : 0.9)};
  outline: none;

  &:focus {
    outline: 3px solid rgba(11, 100, 209, 0.12);
  }
`;

/* -------------------------
   Sample images (replace with your real images)
   ------------------------- */
const IMAGES = [
  {
    title: "Award Ceremony",
    src: Slide1,
    caption: "Annual recognition awards 2024",
  },
  {
    title: "Award Ceremony",
    src: Slide2,
    caption: "Annual recognition awards 2024",
  },
  {
    title: "Operational Excellence",
    src: Slide3,
    caption: "Process & compliance excellence",
  },
  {
    title: "Top Performer",
    src: Slide4,
    caption: "Top performer awards",
  },
  {
    title: "Recognition Day",
    src: Slide5,
    caption: "Team recognition day",
  },
  {
    title: "Recognition Day",
    src: Slide6,
    caption: "Team recognition day",
  },
  {
    title: "Recognition Day",
    src: Slide7,
    caption: "Team recognition day",
  },
  {
    title: "Recognition Day",
    src: Slide8,
    caption: "Team recognition day",
  },
  {
    title: "Recognition Day",
    src: Slide9,
    caption: "Team recognition day",
  },
];

/* -------------------------
   Component
   ------------------------- */
export default function AwardsCarousel({
  autoplay = true,
  autoplayDelay = 3500, // ms
  photos = [],
}) {
  const [index, setIndex] = useState(0);
  const images = photos.length
    ? photos.map((photo) => ({
        title: photo.title || photo.category || "Gallery",
        src: photo.image,
        caption: photo.category || "K Shyam G Management",
      }))
    : IMAGES;
  const total = images.length;
  const slidesRef = useRef(null);
  const isPaused = useRef(false);

  const goTo = (i) => {
    const next = (i + total) % total;
    setIndex(next);
  };
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  useEffect(() => {
    if (!autoplay) return;
    if (isPaused.current) return;

    const id = setTimeout(() => {
      setIndex((v) => (v + 1) % total);
    }, autoplayDelay);

    return () => clearTimeout(id);
  }, [index, autoplay, autoplayDelay, total]);

  const pauseAutoplay = () => (isPaused.current = true);
  const resumeAutoplay = () => (isPaused.current = false);

  // simple touch/swipe handlers
  useEffect(() => {
    const el = slidesRef.current;
    if (!el) return;
    let startX = 0;
    let deltaX = 0;
    let touching = false;

    const onTouchStart = (e) => {
      touching = true;
      pauseAutoplay();
      startX = e.touches[0].clientX;
    };
    const onTouchMove = (e) => {
      if (!touching) return;
      deltaX = e.touches[0].clientX - startX;
    };
    const onTouchEnd = () => {
      if (!touching) return;
      touching = false;
      if (Math.abs(deltaX) > 60) {
        deltaX < 0 ? next() : prev();
      }
      deltaX = 0;
      resumeAutoplay();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total]);

  // keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        prev();
        pauseAutoplay();
      }
      if (e.key === "ArrowRight") {
        next();
        pauseAutoplay();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const translateX = `translateX(-${index * 100}%)`;

  return (
    <Container
      id="awards"
      aria-roledescription="carousel"
      aria-label="Team & Awards"
    >
      <h2>Team & Awards</h2>

      <p>
        Excellence in collections, compliance and teamwork — showcased through
        awards and the people behind the performance.
      </p>

      <Viewport
        onMouseEnter={() => pauseAutoplay()}
        onMouseLeave={() => resumeAutoplay()}
        onFocus={() => pauseAutoplay()}
        onBlur={() => resumeAutoplay()}
      >
        <Slides ref={slidesRef} style={{ transform: translateX }}>
          {images.map((img, i) => (
            <Frame
              key={i}
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${total} — ${img.title}`}
            >
              <Card role="group" aria-label={img.title}>
                <Badge aria-hidden>{String(i + 1).padStart(2, "0")}</Badge>

                <ImgBox>
                  <img src={img.src} alt={img.title} loading="lazy" />
                  {/* <ImageTag aria-hidden>{img.title}</ImageTag> */}
                </ImgBox>

                {/* <Content>
                  <CardTitle>{img.title}</CardTitle>
                  <CardCaption>{img.caption}</CardCaption>
                </Content> */}
              </Card>
            </Frame>
          ))}
        </Slides>

        <Arrow
          aria-label="Previous slide"
          onClick={() => {
            prev();
            pauseAutoplay();
          }}
          data-dir="prev"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M15 6l-6 6 6 6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Arrow>

        <Arrow
          aria-label="Next slide"
          onClick={() => {
            next();
            pauseAutoplay();
          }}
          data-dir="next"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Arrow>
      </Viewport>

      <Dots role="tablist" aria-label="Carousel pages">
        {images.map((_, i) => (
          <Dot
            key={i}
            active={i === index}
            aria-label={`Go to slide ${i + 1}`}
            aria-pressed={i === index}
            onClick={() => {
              goTo(i);
              pauseAutoplay();
            }}
          />
        ))}
      </Dots>
    </Container>
  );
}
